import Balancer from "react-wrap-balancer";
import { useRouter } from "next/router";
import React from "react";
import ReactMarkdown from "markdown-to-jsx";

import type { BriefingsAboutData } from "content-ui/briefings/fetchers/briefingsAboutFetcher";
import Button from "components/buttonNew/button";
import BlurUpImage from "components/blurUpImage/blurUpImage";
import Markdown from "components/markdown/markdown";
import * as Accordion from "components/accordion/accordion";

import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";

import FeedbackSlider from "./feedbackSlider/feedbackSlider";
import SponsorImage from "./sponsorImage/sponsorImage";
import { Marquee } from "components/marquee/marquee";

type SectionPromoProps = Pick<
  BriefingsAboutData,
  | "promoImage"
  | "primaryCtaUrl"
  | "primaryCtaLabel"
  | "secondaryButtonUrl"
  | "secondaryButtonLabel"
  | "promoSectionHeading"
  | "feedbacksHeading"
  | "feedbacks"
  | "sponsors"
  | "faq"
  | "faqHeading"
>;

const SectionPromo = ({
  promoSectionHeading,
  secondaryButtonUrl,
  secondaryButtonLabel,
  primaryCtaUrl,
  primaryCtaLabel,
  promoImage,
  feedbacksHeading,
  faqHeading,
  feedbacks,
  faq,
  sponsors,
}: SectionPromoProps) => {
  const { track } = useMixpanelTracking();
  const router = useRouter();

  const handleTracking = async (
    event: string,
    {
      text,
      url,
    }: {
      text: string;
      url: string;
    }
  ) => {
    await track(event, {
      "Click text": text,
      "Click link": url,
      "Page path": router.asPath,
      Component: "SectionPromo",
    });
  };

  const handleClickOnHeaderLink = async (
    e: React.MouseEvent<HTMLHeadingElement, MouseEvent>
  ) => {
    if (e.target && (e.target as HTMLHeadingElement).getAttribute("href")) {
      await handleTracking("Briefings about link click", {
        url: (e.target as HTMLHeadingElement).getAttribute("href") as string,
        text:
          (e.target as HTMLHeadingElement).innerText ||
          (e.target as HTMLHeadingElement).innerHTML,
      });
    }
  };

  return (
    <div className={"relative pb-[110px] pt-[70px]"}>
      <div className="max-w-[1140px] overflow-x-hidden px-5 md:mx-auto md:pr-0 lg:max-w-[1220px] lg:px-0">
        <div className={"relative mb-[70px] md:mb-[210px]"}>
          <div
            className={
              "relative z-[1] flex max-w-[300px] flex-col md:max-w-[450px] lg:max-w-[38%]"
            }
          >
            {promoSectionHeading && (
              <h2 className="mb-[30px] font-recklessBold text-[32px] leading-[130%] text-pro-blue md:mb-[70px] md:text-[52px]">
                <Balancer>{promoSectionHeading}</Balancer>
              </h2>
            )}
            <div className={"flex flex-col gap-x-[10px] gap-y-[20px]"}>
              {secondaryButtonLabel && secondaryButtonUrl && (
                <div className={"inline-flex"}>
                  <Button
                    size={"medium"}
                    component={"link"}
                    colour={"black"}
                    href={secondaryButtonUrl}
                    className={"ga-briefings-about-cta-secondary"}
                    onClick={async () =>
                      await handleTracking("Briefings about link click", {
                        text: secondaryButtonLabel,
                        url: secondaryButtonUrl,
                      })
                    }
                  >
                    {secondaryButtonLabel}
                  </Button>
                </div>
              )}
              <div className={"inline-flex"}>
                <Button
                  size={"medium"}
                  component={"link"}
                  colour={"navy"}
                  href={primaryCtaUrl}
                  className={"ga-briefings-about-cta"}
                  onClick={async () =>
                    await handleTracking("Briefings about link click", {
                      text: primaryCtaLabel,
                      url: primaryCtaUrl,
                    })
                  }
                >
                  {primaryCtaLabel}
                </Button>
              </div>
            </div>
          </div>
          {promoImage && promoImage.url && (
            <div
              className={
                "ml-[20px] mt-[-92px] h-[246px] w-[605px] md:absolute md:bottom-[-70px] md:left-[191px] md:ml-0 md:mt-0 md:h-[410px] md:w-auto lg:left-auto lg:right-0"
              }
            >
              <BlurUpImage
                src={promoImage.url}
                alt={promoImage.alt}
                width={promoImage.width}
                height={promoImage.height}
                blurDataURL={promoImage.blurUpThumb || undefined}
                className={
                  "pointer-events-none h-100 w-100 object-contain object-left md:object-cover lg:object-right"
                }
              />
            </div>
          )}
        </div>
      </div>
      <div className="max-w-[1140px] overflow-x-hidden px-5 md:mx-auto lg:max-w-[1220px] lg:px-0">
        <div>
          {feedbacks.length > 0 && (
            <div
              className={
                "mb-[30px] flex flex-wrap justify-between gap-[24px] lg:flex-nowrap lg:gap-0"
              }
            >
              {feedbacksHeading && (
                <div className={"w-full lg:max-w-[400px]"}>
                  <h2
                    className={
                      "w-full pr-4 font-recklessBold text-[32px] leading-[130%]"
                    }
                  >
                    <Balancer>{feedbacksHeading}</Balancer>
                  </h2>
                </div>
              )}
              {feedbacks && feedbacks.length > 0 && (
                <div className={"m-auto w-full md:w-[720px] lg:m-0"}>
                  <FeedbackSlider feedbacks={feedbacks} />
                </div>
              )}
            </div>
          )}
          {sponsors.length > 0 && (
            <div className={"mb-[50px] mt-[30px] pb-lg lg:mb-[100px]"}>
              <Marquee animationSpeed={44}>
                {sponsors.map((sponsor) => (
                  <div key={sponsor.id}>
                    <SponsorImage {...sponsor} />
                  </div>
                ))}
              </Marquee>
            </div>
          )}
          <div
            className={
              "mt-[50px] flex flex-col flex-nowrap gap-[30px] lg:flex-row lg:gap-[66px]"
            }
          >
            <div className={"w-full max-w-[458px] lg:max-w-[315px]"}>
              {faqHeading && (
                <h2
                  className={"font-recklessBold text-[32px] leading-[130%]"}
                  onClick={async (e) => handleClickOnHeaderLink(e)}
                >
                  <ReactMarkdown
                    options={{ forceBlock: true }}
                    className={
                      "[&_a]:inline [&_a]:text-pro-blue [&_a]:underline [&_a]:[text-underline-position:under] focus:[&_a]:outline-none focus-visible:[&_a]:outline-none focus-visible:[&_a]:ring focus-visible:[&_a]:ring-pro-blue"
                    }
                  >
                    {faqHeading}
                  </ReactMarkdown>
                </h2>
              )}
            </div>
            <div className={"w-full"}>
              {faq && faq.length > 0 && (
                <Accordion.Root type={"single"} collapsible={true}>
                  {faq.map(({ question, answer, id }) => {
                    return (
                      <Accordion.Item key={id} value={id}>
                        <Accordion.Trigger>{question}</Accordion.Trigger>
                        <Accordion.Content>
                          <Markdown text={answer} />
                        </Accordion.Content>
                      </Accordion.Item>
                    );
                  })}
                </Accordion.Root>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionPromo;
